/**
 * Mein Konto — Custom Account Backend
 *
 * Reads session from localStorage key "b2b_session".
 * Falls back to "not logged in" screen if missing or expired.
 *
 * Data sources:
 *  - Session (localStorage)                 → customer info, personal orders
 *  - Pimcore /api/b2b/company-orders        → all company orders (B2B)
 *  - Pimcore /api/b2b/documents             → AB/LS/RE/GS/OP (Abacus-stub)
 *  - Pimcore /api/b2b/favorites             → product watchlist
 *  - Pimcore /api/b2b/contacts              → company contacts
 *  - Pimcore /api/b2b/profile (PUT)         → profile / address update
 *  - Pimcore /api/b2b/orders/{id}/items     → reorder line items
 *  - Shopify /cart/add.js                   → reorder cart
 */
(function () {
  'use strict';

  var SESSION_KEY = 'b2b_session';
  // Riegel gegen doppelte Klick-Registrierung: renderOrders() laeuft ab jetzt
  // zweimal (Momentaufnahme + Nachladen). Ohne das haengen zwei Listener am
  // Container und "Nachbestellen" loest die Nachbestellung DOPPELT aus.
  var ordersClickBound = false;
  var API_BASE    = (window.b2bConfig && window.b2bConfig.apiBase) || 'https://pim.schaltschrank24.ch';

  /**
   * Seitensprache fuer die /api/b2b/*-Aufrufe. Quelle ist b2bConfig.locale
   * (aus request.locale.iso_code, siehe layout/theme.liquid); <html lang> und
   * zuletzt 'de' sind nur Rueckfaelle, falls das Snippet mal nicht laeuft.
   */
  function b2bLocale() {
    return (window.b2bConfig && window.b2bConfig.locale)
        || document.documentElement.getAttribute('lang')
        || 'de';
  }
  var API_KEY     = (window.b2bConfig && window.b2bConfig.apiKey)  || '';

  // ── DOM refs ────────────────────────────────────────────────
  var elLoading          = document.getElementById('konto-loading');
  var elNotLoggedIn      = document.getElementById('konto-not-logged-in');
  var elContent          = document.getElementById('konto-content');
  var elGreeting         = document.getElementById('konto-greeting');
  var elBanner           = document.getElementById('konto-banner');
  var elCompanyInfo      = document.getElementById('konto-company-info');
  var elCompanyName      = document.getElementById('konto-company-name');
  var elCompanyUid       = document.getElementById('konto-company-uid');
  var elOrdersContent    = document.getElementById('konto-orders-content');
  var elReorderMsg       = document.getElementById('konto-reorder-msg');
  var elCoOrdersSection  = document.getElementById('konto-company-orders-section');
  var elCoOrdersContent  = document.getElementById('konto-company-orders-content');
  var elDocsSection      = document.getElementById('konto-documents-section');
  var elDocsContent      = document.getElementById('konto-documents-content');
  var elDocTabs          = document.getElementById('konto-doc-tabs');
  var elFavSection       = document.getElementById('konto-favorites-section');
  var elContactsSection  = document.getElementById('konto-contacts-section');
  var elContactsContent  = document.getElementById('konto-contacts-content');
  var elDataGrid          = document.getElementById('konto-data-grid');
  var elAddressesSection  = document.getElementById('konto-addresses-section');
  var elAddressesContent  = document.getElementById('konto-addresses-content');
  var elLogoutBtn         = document.getElementById('konto-logout-btn');
  var elLoginTrigger     = document.getElementById('konto-login-trigger');

  // ── Bootstrap ───────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', function () {
    // Cross-domain session handoff from pim.schaltschrank24.ch invite activation
    (function () {
      var m = document.cookie.match(/(?:^|;\s*)b2b_pending_session=([^;]+)/);
      if (!m) return;
      try { localStorage.setItem(SESSION_KEY, JSON.stringify(JSON.parse(decodeURIComponent(m[1])))); } catch (e) {}
      var baseDomain = location.hostname.split('.').slice(-2).join('.');
      document.cookie = 'b2b_pending_session=; domain=' + baseDomain + '; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    }());

    // Password reset flow: ?resetToken=customerId:token
    var params     = new URLSearchParams(window.location.search);
    var resetToken = params.get('resetToken');
    if (resetToken) {
      hide(elLoading);
      renderResetForm(resetToken);
      return;
    }

    var session = loadSession();

    if (!session) {
      show(elNotLoggedIn);
      hide(elLoading);
      if (elLoginTrigger) {
        elLoginTrigger.addEventListener('click', function () {
          window.location.href = window.localizeUrl('/') + '?openModal=login';
        });
      }
      return;
    }

    hide(elLoading);
    show(elContent);

    bindDetailClicks();
    renderHeader(session);
    renderBanner(session);
    renderCompanyInfo(session);
    renderOrders(session);      // Momentaufnahme sofort
    loadPersonalOrders(session); // dann der echte Stand
    renderDataAndAddress(session);
    setupProfileEdit(session);

    if (session.company && session.company.id) {
      show(elCoOrdersSection);
      loadCompanyOrders(session.company.id, session.token);
      show(elContactsSection);
      loadContacts(session);
      setupAddContact(session);
      setupContactsDeactivate(session);
    }

    if (session.isB2B || session.isPrivate) {
      show(elDocsSection);
      loadDocuments(session);
    }
    // Favoriten: jeder angemeldete Kunde. Der Abschnitt wird nur SICHTBAR gemacht —
    // gerendert wird er von assets/favorites-list.js, das [data-favorites-app] selbst
    // findet und sich an favorites:changed haengt. Kein Aufruf von hier.
    show(elFavSection);

    show(elAddressesSection);
    loadAddresses(session);
    setupAddressSection(session);

    if (elLogoutBtn) {
      elLogoutBtn.addEventListener('click', doLogout);
    }
  });

  // ── Session helpers ─────────────────────────────────────────
  function loadSession() {
    try {
      var raw = localStorage.getItem(SESSION_KEY);
      if (!raw) return null;
      var s = JSON.parse(raw);
      if (s.expiresAt && new Date(s.expiresAt) < new Date()) {
        localStorage.removeItem(SESSION_KEY);
        if (window.B2BPriceCache) window.B2BPriceCache.clearAll();
        return null;
      }
      return s;
    } catch (e) {
      return null;
    }
  }

  function patchSession(patch) {
    try {
      var s = loadSession();
      if (!s) return;
      Object.assign(s, patch);
      if (patch.address) s.address = Object.assign(s.address || {}, patch.address);
      localStorage.setItem(SESSION_KEY, JSON.stringify(s));
    } catch (e) {}
  }

  // ── Render helpers ───────────────────────────────────────────
  function renderHeader(s) {
    var name = (s.firstName + ' ' + s.lastName).trim() || s.email;
    if (elGreeting) elGreeting.textContent = (window.customStrings.welcome || 'Willkommen,') + ' ' + name;
  }

  function renderBanner(s) {
    if (!elBanner) return;
    if (s.isDisabled) {
      elBanner.innerHTML = window.customStrings.accountLockedHtml;
      elBanner.className = 'konto-banner konto-banner--error';
      show(elBanner);
    } else if (s.isUnverified) {
      renderVerifyCard(s);
      // Gemeinsame Statusquelle mit der Erinnerungsleiste (assets/verify-reminder.js,
      // 06.10.2026): das isUnverified oben ist die Momentaufnahme vom Login. Ist der Kunde
      // inzwischen freigeschaltet, verschwindet die Karte; hat er schon angefragt (auch
      // ueber die Leiste), zeigt sie "gesendet" statt des Knopfes.
      if (window.B2BVerify) {
        document.addEventListener('b2b:verifiziert', function () {
          s.isUnverified = false;
          elBanner.innerHTML = '';
          hide(elBanner);
        });
        document.addEventListener('b2b:verify-angefragt', showVerifySent);
        window.B2BVerify.status();
      }
    }
  }

  function showVerifySent() {
    var area = document.getElementById('konto-verify-action');
    if (!area) return;
    area.innerHTML =
      '<div class="konto-verify-card__sent">'
      + '<span class="konto-verify-card__check" aria-hidden="true">&#10003;</span>'
      + window.customStrings.vcSent
      + '</div>';
  }

  function renderVerifyCard(s) {
    if (!elBanner) return;
    elBanner.className = '';
    elBanner.style.display = '';
    elBanner.innerHTML =
      '<div class="konto-verify-card">'
      + '<div class="konto-verify-card__icon" aria-hidden="true">&#10003;</div>'
      + '<div class="konto-verify-card__body">'
      +   '<h3 class="konto-verify-card__title">' + window.customStrings.vcTitle + '</h3>'
      +   '<p class="konto-verify-card__lead">' + window.customStrings.vcLead + '</p>'
      +   '<ul class="konto-verify-card__features">'
      +     '<li><span class="konto-verify-card__check" aria-hidden="true">&#10003;</span> ' + window.customStrings.vcF1 + '</li>'
      +     '<li><span class="konto-verify-card__check" aria-hidden="true">&#10003;</span> ' + window.customStrings.vcF2 + '</li>'
      +     '<li><span class="konto-verify-card__check" aria-hidden="true">&#10003;</span> ' + window.customStrings.vcF3 + '</li>'
      +     '<li><span class="konto-verify-card__check" aria-hidden="true">&#10003;</span> ' + window.customStrings.vcF4 + '</li>'
      +     '<li><span class="konto-verify-card__check" aria-hidden="true">&#10003;</span> ' + window.customStrings.vcF5 + '</li>'
      +   '</ul>'
      +   '<div id="konto-verify-action" class="konto-verify-card__action">'
      +     '<button type="button" class="konto-btn konto-verify-card__btn" id="konto-verify-btn">' + window.customStrings.vcBtn + '</button>'
      +     '<p class="konto-verify-card__hint">' + window.customStrings.vcHint + '</p>'
      +   '</div>'
      + '</div>'
      + '</div>';

    // Schon angefragt (hier oder ueber die Leiste) → gleich "gesendet" zeigen.
    if (window.B2BVerify && window.B2BVerify.angefragt(s.customerId)) {
      showVerifySent();
      return;
    }

    var btn = document.getElementById('konto-verify-btn');
    if (!btn) return;
    btn.addEventListener('click', function () {
      btn.disabled = true;
      btn.textContent = window.customStrings.sending;

      // Mit B2BVerify: derselbe Aufruf wie die Leiste; Erfolg meldet sich ueber die
      // Ereignisse oben (gesendet ODER schon verifiziert). Ohne: der alte Weg.
      var senden = window.B2BVerify
        ? window.B2BVerify.send()
        : fetch(API_BASE + '/api/b2b/verify-request', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: s.email, customerId: s.customerId }),
          }).then(function (r) { return r.json(); });

      senden
        .then(function (data) {
          var area = document.getElementById('konto-verify-action');
          if (!area) return;
          if (data && data.success) {
            if (!window.B2BVerify) showVerifySent();
          } else {
            data = data || {};
            btn.disabled = false;
            btn.textContent = window.customStrings.vcBtn;
            area.insertAdjacentHTML('beforeend',
              '<p class="konto-verify-card__error">' + esc(data.error || window.customStrings.sendError) + '</p>');
          }
        })
        .catch(function () {
          btn.disabled = false;
          btn.textContent = window.customStrings.vcBtn;
          var area = document.getElementById('konto-verify-action');
          if (area) area.insertAdjacentHTML('beforeend',
            '<p class="konto-verify-card__error">' + window.customStrings.connError + '</p>');
        });
    });
  }

  function renderCompanyInfo(s) {
    if (!s.company || !s.company.name) return;
    if (elCompanyName) elCompanyName.textContent = s.company.name;
    if (elCompanyUid)  elCompanyUid.textContent  = s.company.uid ? 'UID: ' + s.company.uid : '';
    show(elCompanyInfo);
  }

  // ── Orders ──────────────────────────────────────────────────
  function renderOrders(s) {
    if (!elOrdersContent) return;
    var orders = s.orders || [];
    if (!orders.length) {
      elOrdersContent.innerHTML = '<p class="konto-empty">' + (window.customStrings && window.customStrings.noOrders || 'Noch keine Bestellungen vorhanden.') + '</p>';
      return;
    }

    var rows = orders.map(function (o) {
      var date     = o.created_at ? new Date(o.created_at).toLocaleDateString('de-CH') : '—';
      var price    = o.total_price
        ? parseFloat(o.total_price).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + (o.currency || 'CHF')
        : '—';
      // Ein Status statt Zahlung+Versand: der Bearbeitungsstatus kommt aus der
      // Warenwirtschaft (status_label), mit Rueckfall auf den Shopify-Versandstatus,
      // damit bereits gelieferte Bestellungen nicht auf "Eingegangen" zurueckfallen.
      var statusBadge = o.status_label
        ? '<span class="konto-status ' + statusClass(o.status_code) + '">' + esc(o.status_label) + '</span>'
        : fulfillBadge(o.fulfillment_status);
      // Frueher fuehrte die Bestellnummer auf die Shopify-Statusseite — der Kunde verliess
      // dafuer sein Konto, und dort steht nur, WAS bestellt wurde. Jetzt oeffnet der Klick
      // das Detailfenster mit den Positionen UND dem Lieferfortschritt je Zeile.
      var nameCell = '<button type="button" class="konto-linklike" data-details-shop="' + esc(String(o.id)) + '">' + esc(o.name) + '</button>';
      var orderNoCell = o.order_no
        ? '<button type="button" class="konto-linklike" data-details-order="' + esc(String(o.order_no)) + '">' + esc(String(o.order_no)) + '</button>'
        : '—';
      var reorderBtn = '<button type="button" class="konto-btn konto-btn--reorder konto-btn--sm" data-orderid="' + esc(String(o.id)) + '">' + window.customStrings.reorder + '</button>';

      return '<tr>'
        + '<td data-label="' + window.customStrings.thOrder + '">' + nameCell + '</td>'
        // Auftragsnummer aus der Warenwirtschaft. Sie ist die einzige Nummer, die BEIDE
        // Seiten kennen — mit ihr findet der Innendienst die Bestellung sofort wieder.
        // Fehlt die Zuordnung noch, steht hier ein Strich.
        + '<td data-label="' + window.customStrings.thOrderNo + '">' + orderNoCell + '</td>'
        + '<td data-label="' + window.customStrings.thDate + '">' + esc(date) + '</td>'
        + '<td data-label="' + window.customStrings.thStatus + '">' + statusBadge + '</td>'
        + '<td data-label="' + window.customStrings.thAmount + '">' + esc(price) + '</td>'
        + '<td data-label="' + window.customStrings.thActions + '">' + reorderBtn + '</td>'
        + '</tr>';
    }).join('');

    elOrdersContent.innerHTML =
      '<table class="konto-table" role="table">'
      + '<thead><tr>'
      + '<th>' + window.customStrings.thOrder + '</th><th>' + window.customStrings.thOrderNo + '</th><th>' + window.customStrings.thDate + '</th><th>' + window.customStrings.thStatus + '</th><th>' + window.customStrings.thAmount + '</th><th></th>'
      + '</tr></thead>'
      + '<tbody>' + rows + '</tbody>'
      + '</table>';

    // Reorder-Klicks EINMAL am Container binden, nicht bei jedem Rendern erneut.
    // loadSession() steht jetzt im Handler: die Sitzung wird zum Klickzeitpunkt
    // gelesen statt zum Renderzeitpunkt eingefroren.
    if (!ordersClickBound) {
      ordersClickBound = true;
      elOrdersContent.addEventListener('click', function (e) {
        var btn = e.target.closest('.konto-btn--reorder');
        if (!btn) return;
        var orderId = btn.dataset.orderid;
        var session = loadSession();
        if (orderId && session) handleReorder(orderId, session, btn);
      });
    }
  }

  // Farbgebung der Statusplakette. Erfolgszustaende gruen, Storno/Fehler rot,
  // Zwischenzustaende gelb — dieselbe Sprache wie in der Firmentabelle.
  function statusClass(code) {
    if (code === 'versandt' || code === 'geliefert') return 'konto-status--paid';
    if (code === 'storniert') return 'konto-status--refund';
    if (code === 'teilversandt' || code === 'in_pruefung') return 'konto-status--pending';
    return 'konto-status--neutral';
  }

  // ── Persoenliche Bestellungen live nachladen ─────────────────
  //
  // WARUM: session.orders ist eine Momentaufnahme aus dem Login, und die Sitzung
  // haelt 24 Stunden. Ohne dieses Nachladen sieht ein angemeldeter Kunde den Stand
  // von gestern. Die Momentaufnahme wird trotzdem zuerst gemalt, damit die Liste
  // sofort steht statt erst nach dem Netzcall.
  //
  // Faellt der Call aus (kein Token, altes Token ohne E-Mail, Shopify nicht
  // erreichbar), bleibt die Momentaufnahme stehen. Bewusst kein Fehlertext —
  // der bisherige Stand ist besser als eine leere Liste.
  //
  // ⚠️ Ausnahme 401 (Aufgabe #86): das Token ist ungueltig, z. B. nach dem Wechsel des
  // Token-Geheimnisses. Dann Sitzung verwerfen und neu laden — der Kunde sieht sauber
  // „nicht angemeldet" mit Login-Knopf statt eines halb leeren Kontos. Kein Loop: ohne
  // Sitzung laeuft dieser Aufruf gar nicht erst.
  function loadPersonalOrders(s) {
    if (!elOrdersContent || !API_KEY || !s || !s.token) return;

    fetch(API_BASE + '/api/b2b/orders', {
      headers: { 'X-Api-Key': API_KEY, 'Authorization': 'Bearer ' + s.token }
    })
      .then(function (r) {
        if (r.status === 401) {
          try { localStorage.removeItem(SESSION_KEY); } catch (e) {}
          if (window.B2BPriceCache) window.B2BPriceCache.clearAll();
          window.location.reload();
          return null;
        }
        return r.ok ? r.json() : null;
      })
      .then(function (data) {
        if (!data || !data.orders) return;
        patchSession({ orders: data.orders });
        renderOrders({ orders: data.orders });
      })
      .catch(function () { /* Momentaufnahme bleibt stehen */ });
  }

  function handleReorder(orderId, session, btn) {
    btn.disabled = true;
    btn.textContent = '…';
    showReorderMsg('', '');

    fetch(API_BASE + '/api/b2b/orders/' + orderId + '/items?email=' + encodeURIComponent(session.email) + '&customerId=' + session.customerId)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var items = data.items || [];
        if (!items.length) {
          showReorderMsg(window.customStrings.reorderEmpty, 'error');
          btn.disabled = false;
          btn.textContent = window.customStrings.reorder;
          return;
        }

        // Add items to Shopify cart
        var cartItems = items
          .filter(function (i) { return i.variantId; })
          .map(function (i) { return { id: i.variantId, quantity: i.quantity }; });

        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: cartItems }),
        }).then(function (r) {
          if (r.ok) {
            showReorderMsg(window.customStrings.reorderAdded, 'success');
            setTimeout(function () { window.location.href = window.localizeUrl('/cart'); }, 1200);
          } else {
            showReorderMsg(window.customStrings.reorderPartial, 'error');
            btn.disabled = false;
            btn.textContent = window.customStrings.reorder;
          }
        });
      })
      .catch(function () {
        showReorderMsg(window.customStrings.reorderError, 'error');
        btn.disabled = false;
        btn.textContent = window.customStrings.reorder;
      });
  }

  function showReorderMsg(text, type) {
    if (!elReorderMsg) return;
    if (!text) { elReorderMsg.style.display = 'none'; return; }
    elReorderMsg.textContent = text;
    elReorderMsg.style.background = type === 'success' ? '#d1fae5' : '#fee2e2';
    elReorderMsg.style.color      = type === 'success' ? '#065f46' : '#991b1b';
    elReorderMsg.style.border     = '1px solid ' + (type === 'success' ? '#6ee7b7' : '#fca5a5');
    elReorderMsg.style.display    = 'block';
  }

  // ── Detailfenster: was genau bestellt wurde ──────────────────
  //
  // Bewusst KEIN <dialog>: eine Autoren-Regel auf `display` schlaegt dort die eingebaute
  // `:not([open]){display:none}` und das Fenster bleibt nach dem Schliessen sichtbar (im
  // Theme schon einmal passiert). Hier steuert dieselbe show()/hide()-Mechanik wie im
  // restlichen Konto, und der Zustand ist an der Sichtbarkeit ablesbar, nicht an einem Attribut.
  var elDetails = null;
  var detailsBound = false;
  var coShowMoreBound = false;

  function ensureDetailsBox() {
    if (elDetails) return elDetails;
    elDetails = document.createElement('div');
    elDetails.className = 'konto-details-overlay';
    elDetails.setAttribute('role', 'dialog');
    elDetails.setAttribute('aria-modal', 'true');
    elDetails.style.cssText = 'display:none;position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,.55);'
      + 'align-items:flex-start;justify-content:center;padding:4rem 1.5rem;overflow:auto';
    elDetails.innerHTML =
      '<div class="konto-details-box" style="background:#fff;border-radius:6px;max-width:96rem;width:100%;'
      + 'box-shadow:0 1rem 4rem rgba(0,0,0,.3);padding:2.4rem">'
      + '<div style="display:flex;align-items:flex-start;justify-content:space-between;gap:1.6rem">'
      + '<h3 class="konto-details-title" style="margin:0;font-size:2rem"></h3>'
      + '<button type="button" class="konto-details-close konto-btn konto-btn--sm" aria-label="'
      + esc(window.customStrings.odClose) + '">' + esc(window.customStrings.odClose) + '</button>'
      + '</div>'
      + '<div class="konto-details-body" style="margin-top:1.6rem"></div>'
      + '</div>';
    document.body.appendChild(elDetails);

    elDetails.addEventListener('click', function (e) {
      var doc = e.target.closest('.konto-doc');
      if (doc) { ladeBeleg(doc); return; }
      // Klick auf den Schatten schliesst — aber nur dort, nicht im Kasten selbst.
      if (e.target === elDetails || e.target.closest('.konto-details-close')) closeDetails();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && elDetails && elDetails.style.display !== 'none') closeDetails();
    });
    return elDetails;
  }

  function closeDetails() {
    if (!elDetails) return;
    elDetails.style.display = 'none';
    document.documentElement.style.overflow = '';
  }

  function openDetails(query, titel) {
    var box = ensureDetailsBox();
    var session = loadSession();
    if (!session || !session.token) return;

    box.querySelector('.konto-details-title').textContent = titel;
    box.querySelector('.konto-details-body').innerHTML =
      '<p class="konto-empty">' + esc(window.customStrings.odLoading) + '</p>';
    box.style.display = 'flex';
    // Hintergrund festhalten, damit die Seite unter dem Fenster nicht mitscrollt.
    document.documentElement.style.overflow = 'hidden';

    fetch(API_BASE + '/api/b2b/order-details?' + query, {
      headers: { 'X-Api-Key': API_KEY, 'Authorization': 'Bearer ' + session.token }
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.lines) {
          box.querySelector('.konto-details-body').innerHTML =
            '<p class="konto-empty" style="color:#c00">' + esc(window.customStrings.odError) + '</p>';
          return;
        }
        box.querySelector('.konto-details-body').innerHTML = renderDetailLines(data);
      })
      .catch(function () {
        box.querySelector('.konto-details-body').innerHTML =
          '<p class="konto-empty" style="color:#c00">' + esc(window.customStrings.odError) + '</p>';
      });
  }

  function renderDetailLines(data) {
    if (!data.lines.length) {
      return '<p class="konto-empty">' + esc(window.customStrings.odEmpty) + '</p>';
    }
    var fr = b2bLocale() === 'fr';
    var summe = 0;
    // Lieferspalten nur zeigen, wenn die Warenwirtschaft geantwortet hat. Der Shop kennt
    // keinen Lieferfortschritt — eine Spalte voller Striche behauptet sonst, es sei nichts
    // unterwegs.
    var mitLieferung = data.source === 'abacus';

    var zeilen = data.lines.map(function (l) {
      summe += (typeof l.line_total === 'number') ? l.line_total : 0;
      var name = (fr && l.name_fr) ? l.name_fr : l.name;
      var detail = l.detail ? '<div style="color:#6b7280;font-size:1.25rem">' + esc(l.detail) + '</div>' : '';
      var geliefert = (l.delivered === null || typeof l.delivered === 'undefined') ? '—' : zahl(l.delivered);
      var offen = (l.backlog === null || typeof l.backlog === 'undefined') ? '—' : zahl(l.backlog);
      var ls = l.delivery_note
        ? '<div style="color:#6b7280;font-size:1.25rem">' + esc(window.customStrings.odDeliveryNote) + ' ' + esc(String(l.delivery_note)) + '</div>'
        : '';
      return '<tr>'
        + '<td data-label="' + window.customStrings.odColItem + '"><code>' + esc(l.sku) + '</code></td>'
        + '<td data-label="' + window.customStrings.odColDesc + '">' + esc(name) + detail + ls + '</td>'
        + '<td data-label="' + window.customStrings.odColOrdered + '">' + zahl(l.ordered) + '</td>'
        + (mitLieferung ? '<td data-label="' + window.customStrings.odColDelivered + '">' + geliefert + '</td>' : '')
        + (mitLieferung ? '<td data-label="' + window.customStrings.odColOpen + '">' + offen + '</td>' : '')
        + '<td data-label="' + window.customStrings.odColPrice + '">' + (l.unit_price === null ? '—' : geld(l.unit_price)) + '</td>'
        + '<td data-label="' + window.customStrings.odColTotal + '">' + geld(l.line_total) + '</td>'
        + '</tr>';
    }).join('');

    var belege = renderBelege(data);

    // Lageretiketten (Aufgabe #59): „Etiketten fuer diese Bestellung". Unsichtbar, bis
    // konto-etiketten.js die Freigabe hat (html.etiketten-bereit, CSS in main-konto.liquid);
    // den Klick empfaengt konto-etiketten.js. Die SKUs stehen JSON-kodiert im Attribut.
    var etikettSkus = [];
    data.lines.forEach(function (l) {
      if (l.sku && etikettSkus.indexOf(l.sku) === -1) { etikettSkus.push(l.sku); }
    });
    var etikettKnopf = etikettSkus.length
      ? '<p style="margin:1.2rem 0 0"><button type="button" class="konto-btn konto-btn--outline konto-btn--sm konto-et-aus-bestellung"'
        + ' data-skus="' + esc(JSON.stringify(etikettSkus)) + '">Etiketten für diese Bestellung</button></p>'
      : '';

    return '<div style="overflow-x:auto">'
      + '<table class="konto-table" role="table"><thead><tr>'
      + '<th>' + window.customStrings.odColItem + '</th>'
      + '<th>' + window.customStrings.odColDesc + '</th>'
      + '<th>' + window.customStrings.odColOrdered + '</th>'
      + (mitLieferung ? '<th>' + window.customStrings.odColDelivered + '</th>' : '')
      + (mitLieferung ? '<th>' + window.customStrings.odColOpen + '</th>' : '')
      + '<th>' + window.customStrings.odColPrice + '</th>'
      + '<th>' + window.customStrings.odColTotal + '</th>'
      + '</tr></thead><tbody>' + zeilen + '</tbody></table></div>'
      + '<p style="margin:1.2rem 0 0;text-align:right;font-weight:600">'
      + esc(window.customStrings.odTotalNet) + ' ' + geld(summe) + '</p>'
      + etikettKnopf
      + belege;
  }

  // Belege: Auftragsbestaetigung, Lieferschein, Rechnung.
  // `documents` ist null, wenn die Warenwirtschaft den Abruf abgelehnt hat — das ist etwas
  // anderes als eine leere Liste und bekommt deshalb einen anderen Satz. Sonst behauptet
  // eine Stoerung, es gaebe keine Belege.
  function renderBelege(data) {
    var kopf = '<h4 style="margin:2rem 0 .8rem;font-size:1.5rem">' + esc(window.customStrings.docSection) + '</h4>';

    if (data.documents === null || typeof data.documents === 'undefined') {
      return kopf + '<p class="konto-empty" style="margin:0">' + esc(window.customStrings.docUnavailable) + '</p>';
    }
    if (!data.documents.length) {
      return kopf + '<p class="konto-empty" style="margin:0">' + esc(window.customStrings.docNone) + '</p>';
    }

    var LABELS = {
      'auftragsbestaetigung': window.customStrings.docTypeAuftragsbestaetigung,
      'lieferschein':         window.customStrings.docTypeLieferschein,
      'rechnung':             window.customStrings.docTypeRechnung
    };

    var knoepfe = data.documents.map(function (d) {
      var label = LABELS[d.type] || d.type;
      // Das Belegdatum sagt einem Kunden mehr als die Dateigroesse. Es steckt in der
      // Bezeichnung aus Abacus („Lieferschein Nr. 259 vom 04.09.2026"), auf Franzoesisch
      // hinter „de" statt „vom" — deshalb wird nur nach dem Datum selbst gesucht.
      var treffer = /(\d{1,2}\.\d{1,2}\.\d{4})/.exec(d.title || '');
      var kb = treffer ? ' · ' + treffer[1]
             : (d.size ? ' · ' + Math.round(d.size / 1024) + ' KB' : '');
      return '<button type="button" class="konto-btn konto-btn--sm konto-doc"'
        + ' data-doc-id="' + esc(String(d.id)) + '"'
        + ' data-doc-order="' + esc(String(data.order_no)) + '"'
        + ' data-doc-label="' + esc(label) + '">'
        + esc(label) + '<span style="font-weight:400;opacity:.75">' + esc(kb) + '</span></button>';
    }).join(' ');

    return kopf + '<div style="display:flex;flex-wrap:wrap;gap:.8rem">' + knoepfe + '</div>'
      + '<p class="konto-doc-msg" style="margin:.8rem 0 0;color:#c00;display:none"></p>';
  }

  // ⚠️ Kein einfacher Verweis: der Download braucht den Authorization-Kopf, den ein <a href>
  // nicht mitschicken kann. Also holen, als Blob ablegen und den Klick auf einen unsichtbaren
  // Verweis weiterreichen — so bleibt der Beleg hinter der Anmeldung.
  function ladeBeleg(btn) {
    var session = loadSession();
    if (!session || !session.token) return;
    var box = btn.closest('.konto-details-body, #konto-documents-content');
    var msg = box ? box.querySelector('.konto-doc-msg') : null;
    var beschriftung = btn.innerHTML;
    btn.disabled = true;
    btn.textContent = '…';
    if (msg) msg.style.display = 'none';

    fetch(API_BASE + '/api/b2b/order-document?sales_order_id=' + encodeURIComponent(btn.dataset.docOrder)
        + '&document_id=' + encodeURIComponent(btn.dataset.docId), {
      headers: { 'X-Api-Key': API_KEY, 'Authorization': 'Bearer ' + session.token }
    })
      .then(function (r) { return r.ok ? r.blob() : null; })
      .then(function (blob) {
        btn.disabled = false;
        btn.innerHTML = beschriftung;
        if (!blob) {
          if (msg) { msg.textContent = window.customStrings.docError; msg.style.display = 'block'; }
          return;
        }
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = btn.dataset.docLabel + '.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        // Erst freigeben, wenn der Browser den Download aufgenommen hat.
        setTimeout(function () { URL.revokeObjectURL(url); }, 30000);
      })
      .catch(function () {
        btn.disabled = false;
        btn.innerHTML = beschriftung;
        if (msg) { msg.textContent = window.customStrings.docError; msg.style.display = 'block'; }
      });
  }

  function zahl(n) {
    return esc(Number(n).toLocaleString('de-CH', { maximumFractionDigits: 3 }));
  }

  function geld(n) {
    if (typeof n !== 'number') return '—';
    return esc(n.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' CHF');
  }

  // Klicks EINMAL am Dokument binden: beide Tabellen werden mehrfach neu gezeichnet,
  // eine Bindung je Rendervorgang loeste das Fenster sonst mehrfach aus.
  function bindDetailClicks() {
    if (detailsBound) return;
    detailsBound = true;

    // Die Nummern sind Schaltflaechen, muessen aber aussehen wie der Verweis vorher.
    // Werte woertlich aus `assets/konto.css` (.konto-table a): #dd002e, ohne Unterstrich,
    // Unterstrich erst beim Ueberfahren. Kein eigener Farbton — im Theme existieren fuenf
    // verschiedene Rots, ein sechstes haette die Sprache der Tabelle gebrochen.
    var css = document.createElement('style');
    css.textContent = '.konto-linklike{background:none!important;border:0;padding:0;margin:0;'
      + 'font:inherit;color:#dd002e;text-decoration:none;cursor:pointer;-webkit-appearance:none;appearance:none}'
      + '.konto-linklike:hover{text-decoration:underline}';
    document.head.appendChild(css);
    document.addEventListener('click', function (e) {
      // Beleg-Knoepfe stehen sowohl im Bestellfenster als auch im Dokumente-Abschnitt.
      var beleg = e.target.closest('.konto-doc');
      if (beleg) { ladeBeleg(beleg); return; }
      var shop = e.target.closest('[data-details-shop]');
      if (shop) {
        openDetails('shopify_order_id=' + encodeURIComponent(shop.dataset.detailsShop),
          window.customStrings.odTitle + ' ' + shop.textContent.trim());
        return;
      }
      var auftrag = e.target.closest('[data-details-order]');
      if (auftrag) {
        // ⚠️ KEIN backlog_id=0 mitschicken: die Nummer zaehlt Teillieferungs-Generationen und
        // steht nicht immer auf 0 (SO274 = 1). Die Warenwirtschaft loest sie serverseitig auf.
        openDetails('sales_order_id=' + encodeURIComponent(auftrag.dataset.detailsOrder),
          window.customStrings.odTitle + ' ' + window.customStrings.thOrderNo + ' ' + auftrag.textContent.trim());
      }
    });
  }

  // ── Company Orders ───────────────────────────────────────────
  // Das Login-Token MUSS mit: der Endpunkt leitet die Firma daraus ab und glaubt
  // die mitgeschickte company_id nicht mehr. Ohne Token gibt es 401 — der
  // API-Schluessel allein steht im Quelltext jeder Shopseite und weist niemanden aus.
  function loadCompanyOrders(companyId, token) {
    if (!elCoOrdersContent || !API_KEY) return;
    if (!token) {
      // Sehr alte Sitzung ohne Token. Keine Fehlermeldung — eine neue Anmeldung
      // stellt das Token aus, bis dahin bleibt der Abschnitt leer.
      elCoOrdersContent.innerHTML = '<p class="konto-empty">' + window.customStrings.noCompanyOrders + '</p>';
      return;
    }

    var warningHeader = null;
    fetch(API_BASE + '/api/b2b/company-orders?company_id=' + encodeURIComponent(companyId) + '&limit=50&lang=' + encodeURIComponent(b2bLocale()), {
      headers: { 'X-Api-Key': API_KEY, 'Authorization': 'Bearer ' + token },
    })
      .then(function (r) {
        warningHeader = r.headers.get('X-B2B-Warnings');
        return r.json();
      })
      .then(function (orders) {
        var warnHtml = renderCompanyOrdersWarnings(warningHeader);
        if (!orders.length) {
          elCoOrdersContent.innerHTML = warnHtml + '<p class="konto-empty">' + window.customStrings.noCompanyOrders + '</p>';
          return;
        }

        var STATUS_LABELS = {
          // Leitbild UC-19: 7 offizielle Bestellstatuses
          'abacus-received':       window.customStrings.stReceived,
          'abacus-confirmed':      window.customStrings.stInProgress,
          'abacus-partial-shipped':window.customStrings.stPartialShipped,
          'abacus-shipped':        window.customStrings.stShipped,
          'abacus-delivered':      window.customStrings.stDelivered,
          'abacus-cancelled':      window.customStrings.stCancelled,
          'abacus-review':         window.customStrings.stInReview,
          // Interne / Zahlungs-Statuses
          'abacus-partial':        window.customStrings.stPartialDelivery,
          'payment-completed':     window.customStrings.stPaid,
          'payment-failed':        window.customStrings.stPaymentFailed,
          'pending':               window.customStrings.stPending,
          'synced':                window.customStrings.stSubmitted,
          'partial':               window.customStrings.stPartial,
          'error':                 window.customStrings.stError,
        };
        var STATUS_SUCCESS = ['abacus-shipped', 'abacus-delivered', 'payment-completed'];

        var DOC_STATUS_LABELS = {
          'available':                window.customStrings.stAvailable,
          'not_available_yet':        window.customStrings.stNotYet,
          'blocked_by_order_mapping': window.customStrings.stAfterAbacus,
          'error':                    window.customStrings.stCurrentlyNot,
        };

        // Nach fuenf Zeilen wird eingeklappt. Firmen mit langer Historie schoben sonst alles
        // darunter (Kontakte, Adressen) aus dem Blickfeld; die juengsten Bestellungen sind die,
        // nach denen gesucht wird.
        var SICHTBAR = 5;
        var rows = orders.map(function (o, idx) {
          var statusLabel = o.status_label || STATUS_LABELS[o.status] || o.status || '—';
          var cls   = STATUS_SUCCESS.indexOf(o.status) !== -1 ? 'konto-status--paid'
                    : (o.status === 'abacus-cancelled' || o.status === 'payment-failed' || o.status === 'error') ? 'konto-status--refund'
                    : (o.status === 'abacus-review' || o.status === 'abacus-partial-shipped' || o.status === 'abacus-partial') ? 'konto-status--pending'
                    : 'konto-status--neutral';
          var date  = o.last_synced ? new Date(o.last_synced).toLocaleDateString('de-CH') : '—';
          // Herkunft: 'shop' = ueber den Webshop bestellt, alles andere vom
          // Innendienst erfasst. Die Bezeichnung nennt bewusst kein System.
          var sourceCell = (o.source === 'shop')
            ? '<span style="color:#6b7280">' + esc(window.customStrings.srcShop) + '</span>'
            : '<span class="konto-badge konto-badge--manual" style="display:inline-block;padding:.15rem .5rem;border:1px solid #c7a64a;background:#fff8e1;color:#7a5a00;border-radius:3px;font-size:1.15rem;font-weight:600">' + esc(window.customStrings.srcManual) + '</span>';
          var amount = o.amount_net
            ? parseFloat(o.amount_net).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' CHF'
            : '—';
          var orderDate = o.order_date ? new Date(o.order_date).toLocaleDateString('de-CH') : date;
          var coShopCell = o.shopify_order_id
            ? '<button type="button" class="konto-linklike" data-details-shop="' + esc(String(o.shopify_order_id)) + '">#' + esc(String(o.shopify_order_id)) + '</button>'
            : '—';
          var coOrderCell = o.abacus_order_number
            ? '<button type="button" class="konto-linklike" data-details-order="' + esc(String(o.abacus_order_number)) + '">' + esc(String(o.abacus_order_number)) + '</button>'
            : '—';
          return '<tr' + (idx >= SICHTBAR ? ' class="konto-co-more" style="display:none"' : '') + '>'
            + '<td data-label="' + window.customStrings.thOrder + '">' + coShopCell + '</td>'
            + '<td data-label="' + window.customStrings.thOrderNo + '">' + coOrderCell + '</td>'
            + '<td data-label="' + window.customStrings.thDate + '">' + esc(orderDate) + '</td>'
            + '<td data-label="' + window.customStrings.thAmount + '">' + esc(amount) + '</td>'
            + '<td data-label="' + window.customStrings.thSource + '">' + sourceCell + '</td>'
            + '<td data-label="' + window.customStrings.thStatus + '"><span class="konto-status ' + cls + '">' + esc(statusLabel) + '</span></td>'
            + '<td data-label="' + window.customStrings.thOrderer + '">' + esc(o.customer_email || '—') + '</td>'
            + '</tr>';
        }).join('');

        // Belege bekommen keine eigene Spalte, solange sie nicht abrufbar sind —
        // sieben Zeilen "Noch nicht verfuegbar" untereinander sind keine Information.
        // Stattdessen ein Hinweis unter der Tabelle.
        elCoOrdersContent.innerHTML = warnHtml
          + '<table class="konto-table" role="table">'
          + '<thead><tr>'
          + '<th>' + window.customStrings.thOrder + '</th>'
          + '<th>' + window.customStrings.thOrderNo + '</th>'
          + '<th>' + window.customStrings.thDate + '</th>'
          + '<th>' + window.customStrings.thAmount + '</th>'
          + '<th>' + window.customStrings.thSource + '</th>'
          + '<th>' + window.customStrings.thStatus + '</th>'
          + '<th>' + window.customStrings.thOrderer + '</th>'
          + '</tr></thead>'
          + '<tbody>' + rows + '</tbody>'
          + '</table>'
          + (orders.length > SICHTBAR
              ? '<p style="margin:1.2rem 0 0;text-align:center"><button type="button" class="konto-btn konto-btn--sm konto-co-showmore">'
                + esc(window.customStrings.coShowMore) + ' (' + (orders.length - SICHTBAR) + ')</button></p>'
              : '')
          + '<p style="margin:.7rem 0 0;font-size:1.25rem;color:#6b7280">' + esc(window.customStrings.docsHint) + '</p>';

        // Klick am Abschnitt gebunden, nicht am Knopf: die Tabelle wird bei jedem Laden neu
        // gezeichnet, eine Bindung am Knopf waere nach dem naechsten Rendern verwaist.
        if (!coShowMoreBound) {
          coShowMoreBound = true;
          elCoOrdersContent.addEventListener('click', function (e) {
            var btn = e.target.closest('.konto-co-showmore');
            if (!btn) return;
            var versteckt = elCoOrdersContent.querySelectorAll('tr.konto-co-more');
            for (var i = 0; i < versteckt.length; i++) versteckt[i].style.display = '';
            var box = btn.parentNode;
            if (box && box.parentNode) box.parentNode.removeChild(box);
          });
        }
      })
      .catch(function () {
        elCoOrdersContent.innerHTML = '<p class="konto-empty" style="color:#c00">' + window.customStrings.coLoadError + '</p>';
      });
  }

  function renderCompanyOrdersWarnings(headerValue) {
    if (!headerValue) return '';
    var WARNINGS = {
      'abacus_orders_temporarily_unavailable':
        window.customStrings.abacusLimited,
    };
    var codes = headerValue.split(',').map(function (c) { return c.trim(); }).filter(Boolean);
    if (!codes.length) return '';
    return codes.map(function (c) {
      return '<div class="konto-warning" style="padding:.6rem .8rem;margin-bottom:.8rem;border:1px solid #f5d98a;background:#fff8e1;color:#7a5a00;border-radius:3px;font-size:1.25rem">'
        + esc(WARNINGS[c] || c)
        + '</div>';
    }).join('');
  }

  // ── Documents ────────────────────────────────────────────────
  var docCurrentType = 'AB';
  var docAllData = [];

  function loadDocuments(session) {
    if (!elDocsContent || !API_KEY) {
      renderDocStub();
      return;
    }

    // Der Endpunkt leitet die Firma aus dem Token ab; die E-Mail im Verweis waere nur ein
    // Vorschlag, kein Nachweis. Ohne Token gibt es 401.
    if (!session.token) { renderDocStub(); return; }

    fetch(API_BASE + '/api/b2b/documents?limit=200&lang=' + encodeURIComponent(b2bLocale()), {
      headers: { 'X-Api-Key': API_KEY, 'Authorization': 'Bearer ' + session.token },
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        docAllData = data.documents || data || [];
        if (!docAllData.length) {
          renderDocStub();
          return;
        }

        // Nur Reiter zeigen, hinter denen tatsaechlich Belege liegen. Ein Reiter, der nie
        // etwas anzeigt (Gutschriften, offene Posten — dafuer fehlt uns die Datenquelle),
        // sieht aus wie ein Fehler.
        var vorhanden = {};
        docAllData.forEach(function (d) { vorhanden[d.type] = true; });
        var ersterReiter = null;
        if (elDocTabs) {
          elDocTabs.querySelectorAll('.konto-doc-tab').forEach(function (b) {
            if (vorhanden[b.dataset.type]) {
              b.style.display = '';
              if (!ersterReiter) ersterReiter = b;
            } else {
              b.style.display = 'none';
              b.classList.remove('konto-doc-tab--active');
            }
          });
        }
        if (ersterReiter) {
          elDocTabs.querySelectorAll('.konto-doc-tab').forEach(function (b) { b.classList.remove('konto-doc-tab--active'); });
          ersterReiter.classList.add('konto-doc-tab--active');
          docCurrentType = ersterReiter.dataset.type;
        }

        show(elDocTabs);
        renderDocTable(docCurrentType);

        // Tab click events
        if (elDocTabs) {
          elDocTabs.addEventListener('click', function (e) {
            var btn = e.target.closest('.konto-doc-tab');
            if (!btn) return;
            elDocTabs.querySelectorAll('.konto-doc-tab').forEach(function (b) { b.classList.remove('konto-doc-tab--active'); });
            btn.classList.add('konto-doc-tab--active');
            docCurrentType = btn.dataset.type;
            renderDocTable(docCurrentType);
          });
        }
      })
      .catch(function () { renderDocStub(); });
  }

  function renderDocStub() {
    if (!elDocsContent) return;
    elDocsContent.innerHTML = '<div class="konto-card"><p style="margin:0;opacity:.75;font-size:1.4rem">'
      + window.customStrings.lgDocsComing + '<br>'
      + window.customStrings.contactQuestions + ' <a href="mailto:info@schaltschrank24.ch" style="color:inherit;font-weight:600">info@schaltschrank24.ch</a></p></div>';
  }

  function renderDocTable(type) {
    if (!elDocsContent) return;
    var docs = docAllData.filter(function (d) { return d.type === type; });
    if (!docs.length) {
      elDocsContent.innerHTML = '<p class="konto-empty">' + esc(window.customStrings.docNone) + '</p>';
      return;
    }

    var LABELS = {
      'AB': window.customStrings.docTypeAuftragsbestaetigung,
      'LS': window.customStrings.docTypeLieferschein,
      'RE': window.customStrings.docTypeRechnung
    };

    var rows = docs.map(function (d) {
      var date = d.date ? new Date(d.date).toLocaleDateString('de-CH') : '—';
      var label = LABELS[d.type] || d.type;
      // Kein <a href>: der Download braucht den Authorization-Kopf. Derselbe Weg wie im
      // Bestellfenster — holen, als Blob ablegen, an einen unsichtbaren Verweis reichen.
      var dl = d.document_id
        ? '<button type="button" class="konto-btn konto-btn--outline konto-btn--sm konto-doc"'
          + ' data-doc-id="' + esc(String(d.document_id)) + '"'
          + ' data-doc-order="' + esc(String(d.sales_order_id)) + '"'
          + ' data-doc-label="' + esc(label) + '">' + esc(window.customStrings.docDownload) + '</button>'
        : '—';
      return '<tr>'
        + '<td data-label="' + window.customStrings.docColDocument + '">' + esc(d.title || label) + '</td>'
        + '<td data-label="' + window.customStrings.thOrderNo + '">'
        + (d.sales_order_id
            ? '<button type="button" class="konto-linklike" data-details-order="' + esc(String(d.sales_order_id)) + '">' + esc(String(d.number)) + '</button>'
            : esc(String(d.number || '—')))
        + '</td>'
        + '<td data-label="' + window.customStrings.thDate + '">' + esc(date) + '</td>'
        + '<td data-label="' + window.customStrings.docDownload + '">' + dl + '</td>'
        + '</tr>';
    }).join('');

    elDocsContent.innerHTML = '<table class="konto-table" role="table">'
      + '<thead><tr><th>' + window.customStrings.docColDocument + '</th><th>' + window.customStrings.thOrderNo
      + '</th><th>' + window.customStrings.thDate + '</th><th>' + window.customStrings.docDownload + '</th></tr></thead>'
      + '<tbody>' + rows + '</tbody></table>'
      + '<p class="konto-doc-msg" style="margin:.8rem 0 0;color:#c00;display:none"></p>';
  }

  // ── Favoriten ────────────────────────────────────────────────────────
  //
  // ENTFERNT mit Favoriten v2 (26.08.2026). Hier lagen loadFavorites(),
  // enhanceFavoriteCards() und renderFavActions(): sie fuellten [grid-wishlist]
  // ueber global.js mit Produktkarten und mussten anschliessend JEDE Karte
  // reparieren — das Karten-Markup lieferte ein Add-to-Cart mit `disabled`
  // Varianten-Input und ohne Variantenwaehler, also ein zweiter Abruf je Karte.
  //
  // Den Reiter rendert jetzt assets/favorites-list.js in [data-favorites-app],
  // dieselbe Komponente wie /pages/wishlist.

  // ── Contacts ─────────────────────────────────────────────────
  // `after` laeuft NACH dem Neuzeichnen — eine Meldung davor ginge mit der Tabelle unter.
  function loadContacts(session, after) {
    if (!elContactsContent) return;

    fetch(API_BASE + '/api/b2b/contacts?email=' + encodeURIComponent(session.email) + '&customerId=' + session.customerId)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        renderContacts(data.contacts || [], session, data);
        if (typeof after === 'function') after();
      })
      .catch(function () {
        elContactsContent.innerHTML = '<p class="konto-empty" style="color:#c00">' + window.customStrings.ctLoadError + '</p>';
      });
  }

  function setupContactsDeactivate(session) {
    if (!elContactsContent) return;
    elContactsContent.addEventListener('click', function (e) {
      var deactivateBtn = e.target.closest('.konto-contact-deactivate');
      var reactivateBtn = e.target.closest('.konto-contact-reactivate');
      var roleBtn       = e.target.closest('.konto-contact-role');

      // ── Administratorrechte (Aufgabe #44) ──────────────────────
      // Verlangt das Login-Token: der Server prueft es bei Rechteaenderungen hart.
      // 401 → Sitzung verwerfen und neu laden (wie loadPersonalOrders), 409 → Liste
      // neu laden, weil ein zweiter Admin gleichzeitig etwas geaendert haben kann.
      if (roleBtn) {
        var roleEmail = roleBtn.dataset.email;
        var makeAdmin = roleBtn.dataset.admin === '1';
        var isSelf    = roleBtn.dataset.self === '1';
        if (!roleEmail) return;
        var who = roleBtn.dataset.name || roleEmail;
        var question = makeAdmin ? window.customStrings.ctRoleConfirmGrant
          : (isSelf ? window.customStrings.ctRoleConfirmResign : window.customStrings.ctRoleConfirmRevoke);
        if (!confirm(String(question || '').replace('{name}', who))) return;

        if (!API_KEY || !session.token) {
          try { localStorage.removeItem(SESSION_KEY); } catch (err) {}
          window.location.reload();
          return;
        }

        var roleLabel = roleBtn.textContent;
        roleBtn.disabled = true;
        roleBtn.textContent = '…';

        fetch(API_BASE + '/api/b2b/contacts/role', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Api-Key': API_KEY,
            'Authorization': 'Bearer ' + session.token
          },
          body: JSON.stringify({
            email: session.email,
            customerId: session.customerId,
            contactEmail: roleEmail,
            admin: makeAdmin,
            locale: b2bLocale()
          }),
        })
          .then(function (r) {
            if (r.status === 401) {
              try { localStorage.removeItem(SESSION_KEY); } catch (err) {}
              if (window.B2BPriceCache) window.B2BPriceCache.clearAll();
              window.location.reload();
              return null;
            }
            return r.json().then(function (d) { return { ok: r.ok, status: r.status, data: d }; });
          })
          .then(function (res) {
            if (!res) return;
            if (res.ok) {
              loadContacts(session, function () { showContactsMsg(res.data.message || '', 'success'); });
            } else if (res.status === 409) {
              loadContacts(session, function () { showContactsMsg(res.data.error || window.customStrings.ctRoleError, 'error'); });
            } else {
              showContactsMsg((res.data && res.data.error) || window.customStrings.ctRoleError, 'error');
              roleBtn.disabled = false;
              roleBtn.textContent = roleLabel;
            }
          })
          .catch(function () {
            showContactsMsg(window.customStrings.netShort, 'error');
            roleBtn.disabled = false;
            roleBtn.textContent = roleLabel;
          });
        return;
      }

      if (deactivateBtn) {
        var contactEmail = deactivateBtn.dataset.email;
        if (!contactEmail) return;
        if (!confirm(window.customStrings.ctConfirmPre + contactEmail + window.customStrings.ctConfirmPost)) return;
        deactivateBtn.disabled = true;
        deactivateBtn.textContent = '…';

        fetch(API_BASE + '/api/b2b/contacts?email=' + encodeURIComponent(session.email)
          + '&customerId=' + session.customerId
          + '&contactEmail=' + encodeURIComponent(contactEmail), {
          method: 'DELETE',
        })
          .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
          .then(function (res) {
            if (res.ok) {
              loadContacts(session);
            } else {
              showContactsMsg(res.data.error || window.customStrings.errorShort, 'error');
              deactivateBtn.disabled = false;
              deactivateBtn.textContent = 'Deaktivieren';
            }
          })
          .catch(function () {
            showContactsMsg(window.customStrings.netShort, 'error');
            deactivateBtn.disabled = false;
            deactivateBtn.textContent = 'Deaktivieren';
          });
      }

      if (reactivateBtn) {
        var contactEmail = reactivateBtn.dataset.email;
        if (!contactEmail) return;
        if (!confirm(window.customStrings.ctReactivatePre + contactEmail + window.customStrings.ctReactivatePost)) return;
        reactivateBtn.disabled = true;
        reactivateBtn.textContent = '…';

        fetch(API_BASE + '/api/b2b/contacts/reactivate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: session.email, customerId: session.customerId, contactEmail: contactEmail, locale: b2bLocale() }),
        })
          .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
          .then(function (res) {
            if (res.ok) {
              showContactsMsg(window.customStrings.ctReactivated, 'success');
              loadContacts(session);
            } else {
              showContactsMsg(res.data.error || window.customStrings.ctReactivateError, 'error');
              reactivateBtn.disabled = false;
              reactivateBtn.textContent = 'Reaktivieren';
            }
          })
          .catch(function () {
            showContactsMsg(window.customStrings.netShort, 'error');
            reactivateBtn.disabled = false;
            reactivateBtn.textContent = 'Reaktivieren';
          });
      }
    });
  }

  function renderContacts(contacts, session, meta) {
    if (!elContactsContent) return;
    meta = meta || {};
    // Rechte frisch vom Server (nicht aus der Sitzung): nach einem Rollenwechsel
    // stimmt das Login-`isAdmin` nicht mehr. Fehlt das Feld (alter Server-Stand),
    // bleibt alles wie bisher sichtbar — der Server sperrt ohnehin.
    var canManage  = meta.callerIsAdmin !== false;
    var roleEdit   = meta.callerIsAdmin === true;
    var adminCount = typeof meta.adminCount === 'number' ? meta.adminCount : 0;
    var maxAdmins  = typeof meta.maxAdmins === 'number' ? meta.maxAdmins : 2;
    var addArea    = document.getElementById('konto-add-contact-area');
    if (addArea) addArea.style.display = canManage ? '' : 'none';
    // Aufgabe #104: „als Administrator einladen" nur, wo es durchgeht — der Server prueft selbst.
    var adminWrap  = document.getElementById('ac-admin-wrap');
    if (adminWrap) {
      var adminOk = roleEdit && adminCount < maxAdmins;
      adminWrap.style.display = adminOk ? '' : 'none';
      if (!adminOk) { var adminBox = document.getElementById('ac-admin'); if (adminBox) adminBox.checked = false; }
    }

    if (!contacts.length) {
      elContactsContent.innerHTML = '<p class="konto-empty">' + window.customStrings.ctNone + '</p>';
      return;
    }

    var rows = contacts.map(function (c) {
      var name = [c.firstName, c.lastName].filter(Boolean).join(' ') || '—';
      var isInactive = (c.status === 'inactive');
      var isAdmin = (c.isAdmin === true || c.role === 'admin');
      var counted = (c.status === 'active' || c.status === 'invited');
      var jobTitle = (typeof c.jobTitle !== 'undefined')
        ? (c.jobTitle || '')
        : ((c.role && c.role !== 'admin' && c.role !== 'standard_user') ? c.role : '');

      // Knopf nur, wo die Regel auch durchgeht — der Server prueft trotzdem selbst.
      var roleBtn = '';
      if (roleEdit && isAdmin && counted && adminCount > 1) {
        roleBtn = '<button type="button" class="konto-btn konto-btn--outline konto-btn--sm konto-contact-role" data-admin="0"'
          + ' data-self="' + (c.isOwn ? '1' : '0') + '" data-email="' + esc(c.email) + '" data-name="' + esc(name !== '—' ? name : c.email) + '">'
          + (c.isOwn ? window.customStrings.ctRoleResign : window.customStrings.ctRoleRevoke) + '</button>';
      } else if (roleEdit && !isAdmin && c.status === 'active' && adminCount < maxAdmins) {
        roleBtn = '<button type="button" class="konto-btn konto-btn--outline konto-btn--sm konto-contact-role" data-admin="1"'
          + ' data-self="0" data-email="' + esc(c.email) + '" data-name="' + esc(name !== '—' ? name : c.email) + '">'
          + window.customStrings.ctRoleGrant + '</button>';
      }

      var roleBadge = isAdmin
        ? ' <span class="konto-contact-badge" style="background:#1a1a1a;color:#fff;border-color:#1a1a1a;font-size:1.05rem">Administrator</span>'
        : '';
      var statusBadge = isInactive
        ? ' <span class="konto-contact-badge konto-contact-badge--inactive" style="color:#999;border-color:#ccc">Deaktiviert</span>'
        : (c.status === 'invited')
          ? ' <span class="konto-contact-badge konto-contact-badge--invited">' + window.customStrings.badgeInvited + '</span>'
          : '';
      var actionBtn = c.isOwn
        ? '<span class="konto-contact-self">' + window.customStrings.selfMarker + '</span>'
        : !canManage
          ? ''
          : isInactive
            ? '<button type="button" class="konto-btn konto-btn--outline konto-btn--sm konto-contact-reactivate" data-email="' + esc(c.email) + '">' + window.customStrings.acReactivate + '</button>'
            : '<button type="button" class="konto-btn konto-btn--outline konto-btn--sm konto-contact-deactivate" data-email="' + esc(c.email) + '">' + window.customStrings.ctDeactivate + '</button>';
      if (roleBtn) actionBtn = actionBtn + (actionBtn ? ' ' : '') + roleBtn;
      var date = c.addedAt ? new Date(c.addedAt).toLocaleDateString('de-CH') : '—';
      var rowStyle = isInactive ? ' style="opacity:.55"' : '';
      return '<tr' + rowStyle + '>'
        + '<td data-label="' + window.customStrings.ctName + '">' + esc(name) + roleBadge + statusBadge + (c.isOwn ? ' <span class="konto-contact-self">' + window.customStrings.selfMarker + '</span>' : '') + '</td>'
        + '<td data-label="' + window.customStrings.ctEmail + '">' + esc(c.email) + '</td>'
        + '<td data-label="' + window.customStrings.ctRole + '">' + esc(jobTitle || '—') + '</td>'
        + '<td data-label="' + window.customStrings.ctPhone + '">' + esc(c.phone || '—') + '</td>'
        + '<td data-label="' + window.customStrings.ctAdded + '">' + esc(date) + '</td>'
        + '<td data-label="' + window.customStrings.ctAction + '">' + actionBtn + '</td>'
        + '</tr>';
    }).join('');

    elContactsContent.innerHTML = '<table class="konto-table" role="table">'
      + '<thead><tr><th>' + window.customStrings.ctName + '</th><th>' + window.customStrings.ctEmail + '</th><th>' + window.customStrings.ctRole + '</th><th>' + window.customStrings.ctPhone + '</th><th>' + window.customStrings.ctAdded + '</th><th>' + window.customStrings.ctAction + '</th></tr></thead>'
      + '<tbody>' + rows + '</tbody></table>'
      + (roleEdit && window.customStrings.ctRoleHint
        ? '<p style="font-size:1.25rem;color:#666;margin:.6rem 0 0">' + esc(String(window.customStrings.ctRoleHint).replace('{max}', maxAdmins)) + '</p>'
        : '')
      + '<div id="konto-contacts-msg" style="display:none;margin-top:.6rem;padding:.6rem .8rem;border-radius:3px;font-size:1.35rem"></div>';
  }

  function setupAddContact(session) {
    var toggleBtn  = document.getElementById('konto-add-contact-toggle');
    var cancelBtn  = document.getElementById('konto-add-contact-cancel');
    var form       = document.getElementById('konto-add-contact-form');
    var msgEl      = document.getElementById('ac-msg');
    if (!toggleBtn || !form) return;

    toggleBtn.addEventListener('click', function () { show(form); hide(toggleBtn); });
    if (cancelBtn) cancelBtn.addEventListener('click', function () { hide(form); show(toggleBtn); if (msgEl) msgEl.style.display = 'none'; });

    var emailInput = document.getElementById('ac-email');
    if (emailInput) {
      emailInput.addEventListener('blur', function () {
        var val = emailInput.value.trim();
        if (!val || !val.includes('@')) return;
        var firstEl = document.getElementById('ac-first');
        var lastEl  = document.getElementById('ac-last');
        if (!firstEl || !lastEl) return;
        if (firstEl.value.trim() || lastEl.value.trim()) return; // don't overwrite manual input
        fetch(API_BASE + '/api/b2b/contact-lookup?email=' + encodeURIComponent(session.email)
          + '&customerId=' + session.customerId
          + '&contactEmail=' + encodeURIComponent(val))
          .then(function (r) { return r.json(); })
          .then(function (d) {
            if (d.found) {
              if (d.firstName) firstEl.value = d.firstName;
              if (d.lastName)  lastEl.value  = d.lastName;
            }
          })
          .catch(function () {});
      });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var submitBtn = form.querySelector('button[type="submit"]');
      var contactEmail = document.getElementById('ac-email').value.trim();
      if (!contactEmail) return;

      // Aufgabe #104: Rechte gleich mitgeben verlangt — wie der Rollenknopf — das Login-Token.
      var adminBox = document.getElementById('ac-admin');
      var asAdmin  = !!(adminBox && adminBox.checked);
      var headers  = { 'Content-Type': 'application/json' };
      if (asAdmin) {
        if (!API_KEY || !session.token) {
          try { localStorage.removeItem(SESSION_KEY); } catch (err) {}
          window.location.reload();
          return;
        }
        headers['X-Api-Key'] = API_KEY;
        headers['Authorization'] = 'Bearer ' + session.token;
      }

      submitBtn.disabled = true;
      submitBtn.textContent = window.customStrings.sending;

      fetch(API_BASE + '/api/b2b/contacts', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          email:      session.email,
          customerId: session.customerId,
          locale:     b2bLocale(),
          contact: {
            email:     contactEmail,
            firstName: document.getElementById('ac-first').value.trim(),
            lastName:  document.getElementById('ac-last').value.trim(),
            // Aufgabe #44: Funktion, KEINE Rechte. Rechte gibt es nur ueber /contacts/role.
            jobTitle:  (document.getElementById('ac-role')  || {}).value ? document.getElementById('ac-role').value.trim()  : undefined,
            phone:     (document.getElementById('ac-phone') || {}).value ? document.getElementById('ac-phone').value.trim() : undefined,
            admin:     asAdmin,
          },
        }),
      })
        .then(function (r) {
          if (r.status === 401 && asAdmin) {
            try { localStorage.removeItem(SESSION_KEY); } catch (err) {}
            if (window.B2BPriceCache) window.B2BPriceCache.clearAll();
            window.location.reload();
            return null;
          }
          return r.json().then(function (d) { return { ok: r.ok, data: d }; });
        })
        .then(function (res) {
          if (!res) return;
          if (res.ok && res.data.success) {
            showAcMsg(res.data.message || window.customStrings.ctAddedToast, 'success');
            form.reset();
            setTimeout(function () { hide(form); show(toggleBtn); loadContacts(session); }, 2000);
          } else {
            showAcMsg(res.data.error || window.customStrings.ctAddError, 'error');
          }
          submitBtn.disabled = false;
          submitBtn.textContent = 'Einladen';
        })
        .catch(function () {
          showAcMsg(window.customStrings.netShort, 'error');
          submitBtn.disabled = false;
          submitBtn.textContent = 'Einladen';
        });
    });

    function showAcMsg(text, type) {
      if (!msgEl) return;
      msgEl.textContent = text;
      msgEl.style.background = type === 'success' ? '#d1fae5' : '#fee2e2';
      msgEl.style.color      = type === 'success' ? '#065f46' : '#991b1b';
      msgEl.style.border     = '1px solid ' + (type === 'success' ? '#6ee7b7' : '#fca5a5');
      msgEl.style.display    = 'block';
    }
  }

  function showContactsMsg(text, type) {
    var el = document.getElementById('konto-contacts-msg');
    if (!el) return;
    el.textContent = text;
    el.style.background = type === 'success' ? '#d1fae5' : '#fee2e2';
    el.style.color      = type === 'success' ? '#065f46' : '#991b1b';
    el.style.border     = '1px solid ' + (type === 'success' ? '#6ee7b7' : '#fca5a5');
    el.style.display    = 'block';
  }

  // ── Profile & Address editing ────────────────────────────────
  function renderDataAndAddress(s) {
    if (elDataGrid) {
      var pairs = [
        ['Name',    (s.firstName + ' ' + s.lastName).trim()],
        ['E-Mail',  s.email],
        ['Telefon', s.phone || '—'],
      ];
      if (s.company && s.company.name) {
        pairs.unshift([window.customStrings.companyPlain, s.company.name]);
      }
      elDataGrid.innerHTML = pairs.map(function (p) {
        return '<dt>' + esc(p[0]) + '</dt><dd>' + esc(p[1]) + '</dd>';
      }).join('');
    }

  }

  function setupProfileEdit(session) {
    // ── Data edit ──────────────────────────────────────────────
    var editDataBtn    = document.getElementById('konto-edit-data-btn');
    var editDataCancel = document.getElementById('konto-edit-data-cancel');
    var editDataForm   = document.getElementById('konto-edit-data-form');
    var edMsg          = document.getElementById('ed-msg');

    if (editDataBtn && editDataForm) {
      editDataBtn.addEventListener('click', function () {
        document.getElementById('ed-first').value = session.firstName || '';
        document.getElementById('ed-last').value  = session.lastName  || '';
        document.getElementById('ed-phone').value = session.phone     || '';
        show(editDataForm);
        hide(editDataBtn);
      });
      if (editDataCancel) {
        editDataCancel.addEventListener('click', function () { hide(editDataForm); show(editDataBtn); });
      }

      editDataForm.addEventListener('submit', function (e) {
        e.preventDefault();
        var submitBtn = editDataForm.querySelector('button[type="submit"]');
        submitBtn.disabled = true;
        submitBtn.textContent = window.customStrings.saving;

        var patch = {
          email:      session.email,
          customerId: session.customerId,
          firstName:  document.getElementById('ed-first').value.trim(),
          lastName:   document.getElementById('ed-last').value.trim(),
          phone:      document.getElementById('ed-phone').value.trim(),
        };

        saveProfile(patch, function (err, updatedPatch) {
          submitBtn.disabled = false;
          submitBtn.textContent = window.customStrings.acSave;
          if (err) {
            showFormMsg(edMsg, err, 'error');
          } else {
            patchSession(updatedPatch);
            session = loadSession();
            renderDataAndAddress(session);
            renderHeader(session);
            hide(editDataForm);
            show(editDataBtn);
          }
        });
      });
    }

  }

  function saveProfile(patch, callback) {
    fetch(API_BASE + '/api/b2b/profile', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (res.ok && res.data.success) {
          callback(null, res.data.patch || {});
        } else {
          callback(res.data.error || window.customStrings.saveError);
        }
      })
      .catch(function () { callback(window.customStrings.netShort); });
  }

  function showFormMsg(el, text, type) {
    if (!el) return;
    el.textContent = text;
    el.style.background = type === 'success' ? '#d1fae5' : '#fee2e2';
    el.style.color      = type === 'success' ? '#065f46' : '#991b1b';
    el.style.border     = '1px solid ' + (type === 'success' ? '#6ee7b7' : '#fca5a5');
    el.style.display    = 'block';
  }

  // ── Addresses ────────────────────────────────────────────────
  function loadAddresses(session) {
    if (!elAddressesContent) return;
    // includeInactive=1: show deactivated addresses so they can be reactivated (Leitbild UC-11)
    fetch(API_BASE + '/api/b2b/addresses?email=' + encodeURIComponent(session.email) + '&customerId=' + session.customerId + '&includeInactive=1')
      .then(function (r) { return r.json(); })
      .then(function (data) { renderAddresses(data.addresses || [], session); })
      .catch(function () {
        elAddressesContent.innerHTML = '<p class="konto-empty" style="color:#c00">' + window.customStrings.addrLoadError + '</p>';
      });
  }

  function renderAddresses(addresses, session) {
    if (!elAddressesContent) return;
    if (!addresses.length) {
      elAddressesContent.innerHTML = '<p class="konto-empty">' + window.customStrings.addrNone + '</p>';
      return;
    }

    var cards = addresses.map(function (a) {
      var bezeichnung = a.company ? '<div class="konto-addr-card__label">' + esc(a.company) + (a.isDefault ? ' <span class="konto-addr-default-badge">Standard</span>' : '') + '</div>' : (a.isDefault ? '<div class="konto-addr-card__label"><span class="konto-addr-default-badge">Standard</span></div>' : '');
      var ansprechpartner = ((a.contactFirstName || '') + ' ' + (a.contactLastName || '')).trim();
      var line1 = a.address1 || '';
      var line2 = a.address2 || '';
      var line3 = ((a.zip || '') + ' ' + (a.city || '')).trim();
      var addrLines = [ansprechpartner, line1, line2, line3].filter(Boolean).map(esc);
      if (a.comment) addrLines.push('<span style="color:#888;font-size:.9em">' + esc(a.comment) + '</span>');
      var addrHtml = addrLines.join('<br>');

      // Leitbild UC-11: Deaktivieren statt Löschen; Reaktivieren möglich
      // Leitbild UC-12: billing/main → nur Änderungsantrag, kein direktes Bearbeiten
      var isProtected = (a.isEditable === false);
      var setDefaultBtn = (a.isDefault || a.isActive === false || isProtected) ? '' : '<button type="button" class="konto-btn konto-btn--outline konto-btn--sm konto-addr-set-default" data-id="' + a.id + '">' + window.customStrings.acSetDefault + '</button>';
      var deactivateBtn = (a.isDefault || a.isActive === false || isProtected) ? '' : '<button type="button" class="konto-btn konto-btn--outline konto-btn--sm konto-addr-delete" data-id="' + a.id + '">' + window.customStrings.ctDeactivate + '</button>';
      var reactivateBtn = (a.isActive === false) ? '<button type="button" class="konto-btn konto-btn--outline konto-btn--sm konto-addr-reactivate" data-id="' + a.id + '">' + window.customStrings.acReactivate + '</button>' : '';
      var editBtn = (a.isActive === false || isProtected) ? '' : '<button type="button" class="konto-btn konto-btn--outline konto-btn--sm konto-addr-edit" data-id="' + a.id + '">' + window.customStrings.acEdit + '</button>';
      // UC-12: Änderungsantrag-Button für geschützte Adressen (billing/main)
      var changeRequestBtn = (isProtected && a.isActive !== false)
        ? '<button type="button" class="konto-btn konto-btn--outline konto-btn--sm konto-addr-change-request" data-id="' + a.id + '" data-type="' + esc(a.addressType || '') + '">' + window.customStrings.acChangeRequest + '</button>'
        : '';

      // UC-12: Inline Änderungsantrag-Formular (nur für billing/main)
      var changeRequestForm = isProtected
        ? '<form class="konto-edit-form konto-addr-change-form" data-id="' + a.id + '" data-type="' + esc(a.addressType || '') + '" style="display:none;margin-top:1rem">'
          + '<p style="font-size:1.25rem;color:#555;margin:0 0 .8rem">' + window.customStrings.crIntroPre + (a.addressType === 'billing' ? window.customStrings.crAddrBilling : window.customStrings.crAddrMain) + window.customStrings.crIntroPost + '</p>'
          + '<div style="display:grid;grid-template-columns:1fr 1fr;gap:.8rem">'
          + '<input type="text" name="streetName"   placeholder="' + window.customStrings.crPhStreet + '" class="konto-input" style="grid-column:1/-1">'
          + '<input type="text" name="houseNumber"  placeholder="' + window.customStrings.crPhHouse + '" class="konto-input">'
          + '<input type="text" name="zip"          placeholder="' + window.customStrings.crPhZip + '" class="konto-input">'
          + '<input type="text" name="city"         placeholder="' + window.customStrings.crPhCity + '" class="konto-input" style="grid-column:1/-1">'
          + '<select name="country" class="konto-input" style="grid-column:1/-1">'
                    + [['CH',window.customStrings.ccCH]].map(function (p) {
              return '<option value="' + p[0] + '" selected>' + p[1] + '</option>';
            }).join('')
          + '</select>'
          + '<textarea name="comment" placeholder="' + window.customStrings.crPhComment + '" class="konto-input" style="grid-column:1/-1;min-height:5rem"></textarea>'
          + '</div>'
          + '<div class="konto-addr-change-msg" style="display:none;margin-top:.6rem;padding:.6rem .8rem;border-radius:3px;font-size:1.35rem"></div>'
          + '<div style="margin-top:.8rem;display:flex;gap:.6rem;flex-wrap:wrap">'
          + '<button type="submit" class="konto-btn">' + window.customStrings.crSubmit + '</button>'
          + '<button type="button" class="konto-btn konto-btn--outline konto-addr-change-cancel" data-id="' + a.id + '">' + window.customStrings.cancelJs + '</button>'
          + '</div>'
          + '</form>'
        : '';

      var editForm = '<form class="konto-edit-form konto-addr-edit-form" data-id="' + a.id + '" style="display:none;margin-top:1rem">'
        + '<div style="display:grid;grid-template-columns:1fr 1fr;gap:.8rem">'
        + '<input type="text" name="company"          value="' + esc(a.company || '') + '" placeholder="' + window.customStrings.efCompany + '" class="konto-input" style="grid-column:1/-1">'
        + '<input type="text" name="contactFirstName" value="' + esc(a.contactFirstName || '') + '" placeholder="' + window.customStrings.efContactFirst + '" class="konto-input">'
        + '<input type="text" name="contactLastName"  value="' + esc(a.contactLastName  || '') + '" placeholder="' + window.customStrings.efContactLast + '" class="konto-input">'
        + '<input type="text" name="address1"         value="' + esc(a.address1 || '') + '" placeholder="' + window.customStrings.efStreet + '" required class="konto-input" style="grid-column:1/-1">'
        + '<input type="text" name="address2"         value="' + esc(a.address2 || '') + '" placeholder="' + window.customStrings.efAddr2 + '" class="konto-input" style="grid-column:1/-1">'
        + '<input type="text" name="zip"              value="' + esc(a.zip || '') + '" placeholder="' + window.customStrings.efZip + '" class="konto-input">'
        + '<input type="text" name="city"             value="' + esc(a.city || '') + '" placeholder="' + window.customStrings.efCity + '" class="konto-input">'
        + '<select name="country" class="konto-input" style="grid-column:1/-1">'
                + [['CH',window.customStrings.ccCH]].map(function (p) {
            return '<option value="' + p[0] + '" selected>' + p[1] + '</option>';
          }).join('')
        + '</select>'
        + '<input type="text" name="phone"   value="' + esc(a.phone   || '') + '" placeholder="' + window.customStrings.efPhone + '" class="konto-input" style="grid-column:1/-1">'
        + '<input type="text" name="comment" value="' + esc(a.comment || '') + '" placeholder="' + window.customStrings.efComment + '" class="konto-input" style="grid-column:1/-1">'
        + '</div>'
        + '<div class="konto-addr-edit-msg" style="display:none;margin-top:.6rem;padding:.6rem .8rem;border-radius:3px;font-size:1.35rem"></div>'
        + '<div style="margin-top:.8rem;display:flex;gap:.6rem;flex-wrap:wrap">'
        + '<button type="submit" class="konto-btn">' + window.customStrings.acSave + '</button>'
        + '<button type="button" class="konto-btn konto-btn--outline konto-addr-edit-cancel" data-id="' + a.id + '">' + window.customStrings.cancelJs + '</button>'
        + '</div>'
        + '</form>';

      var inactiveStyle = (a.isActive === false) ? ' style="opacity:.55"' : '';
      return '<div class="konto-addr-card' + (a.isDefault ? ' konto-addr-card--default' : '') + '" data-id="' + a.id + '"' + inactiveStyle + '>'
        + bezeichnung
        + (a.isActive === false ? '<div style="font-size:1.1rem;color:#999;margin-bottom:.4rem">' + window.customStrings.acDeactivated + '</div>' : '')
        + (isProtected ? '<div style="font-size:1.1rem;color:#6b7280;margin-bottom:.4rem">' + window.customStrings.acInternalOnly + '</div>' : '')
        + '<div class="konto-addr-card__body">' + addrHtml + '</div>'
        + '<div class="konto-addr-card__actions">' + editBtn + setDefaultBtn + deactivateBtn + reactivateBtn + changeRequestBtn + '</div>'
        + editForm
        + changeRequestForm
        + '</div>';
    }).join('');

    elAddressesContent.innerHTML = '<div class="konto-addr-list">' + cards + '</div>'
      + '<div id="konto-addr-msg" style="display:none;margin-top:.8rem;padding:.6rem .8rem;border-radius:3px;font-size:1.35rem"></div>';
  }

  function setupAddressSection(session) {
    if (!elAddressesSection) return;

    // ── Add address form ──────────────────────────────────────
    var addToggleBtn = document.getElementById('konto-add-addr-toggle');
    var addCancelBtn = document.getElementById('konto-add-addr-cancel');
    var addForm      = document.getElementById('konto-add-addr-form');
    var addMsg       = document.getElementById('na-msg');

    if (addToggleBtn && addForm) {
      addToggleBtn.addEventListener('click', function () { show(addForm); hide(addToggleBtn); });
      if (addCancelBtn) addCancelBtn.addEventListener('click', function () { hide(addForm); show(addToggleBtn); addForm.reset(); if (addMsg) addMsg.style.display = 'none'; });

      addForm.addEventListener('submit', function (e) {
        e.preventDefault();
        var submitBtn = addForm.querySelector('button[type="submit"]');
        submitBtn.disabled = true;
        submitBtn.textContent = window.customStrings.saving;

        fetch(API_BASE + '/api/b2b/addresses', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email:             session.email,
            customerId:        session.customerId,
            company:           document.getElementById('na-company').value.trim(),
            contactFirstName:  (document.getElementById('na-contact-first') || {}).value ? document.getElementById('na-contact-first').value.trim() : '',
            contactLastName:   (document.getElementById('na-contact-last')  || {}).value ? document.getElementById('na-contact-last').value.trim()  : '',
            address1:          document.getElementById('na-addr1').value.trim(),
            address2:          document.getElementById('na-addr2').value.trim(),
            zip:               document.getElementById('na-zip').value.trim(),
            city:              document.getElementById('na-city').value.trim(),
            country:           document.getElementById('na-country').value,
            phone:             (document.getElementById('na-phone')   || {}).value ? document.getElementById('na-phone').value.trim()   : '',
            comment:           (document.getElementById('na-comment') || {}).value ? document.getElementById('na-comment').value.trim() : '',
          }),
        })
          .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
          .then(function (res) {
            submitBtn.disabled = false;
            submitBtn.textContent = window.customStrings.acSave;
            if (res.ok && res.data.success) {
              hide(addForm); show(addToggleBtn); addForm.reset();
              renderAddresses(res.data.addresses || [], session);
              patchSession({ addresses: res.data.addresses || [] });
            } else {
              showAddressMsg(addMsg, res.data.error || window.customStrings.errorShort, 'error');
            }
          })
          .catch(function () {
            submitBtn.disabled = false;
            submitBtn.textContent = window.customStrings.acSave;
            showAddressMsg(addMsg, window.customStrings.netShort, 'error');
          });
      });
    }

    // ── Address card actions (edit / set-default / deactivate / reactivate) ─
    elAddressesContent.addEventListener('click', function (e) {
      var editBtn       = e.target.closest('.konto-addr-edit');
      var cancelBtn     = e.target.closest('.konto-addr-edit-cancel');
      var defaultBtn    = e.target.closest('.konto-addr-set-default');
      var deleteBtn     = e.target.closest('.konto-addr-delete');
      var reactivateBtn = e.target.closest('.konto-addr-reactivate');
      var submitBtn     = e.target.closest('.konto-addr-edit-form button[type="submit"]');

      // Open edit form
      if (editBtn) {
        var id = editBtn.dataset.id;
        var form = elAddressesContent.querySelector('.konto-addr-edit-form[data-id="' + id + '"]');
        if (form) { show(form); editBtn.style.display = 'none'; }
        return;
      }

      // Cancel edit
      if (cancelBtn) {
        var id = cancelBtn.dataset.id;
        var form = elAddressesContent.querySelector('.konto-addr-edit-form[data-id="' + id + '"]');
        var openBtn = elAddressesContent.querySelector('.konto-addr-edit[data-id="' + id + '"]');
        if (form) hide(form);
        if (openBtn) openBtn.style.display = '';
        return;
      }

      // Set as default
      if (defaultBtn) {
        var id = parseInt(defaultBtn.dataset.id, 10);
        defaultBtn.disabled = true;
        fetch(API_BASE + '/api/b2b/addresses/' + id + '/default', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: session.email, customerId: session.customerId }),
        })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            if (data.success) {
              var patch = {};
              if (data.defaultAddress) {
                patch.address = {
                  company:  data.defaultAddress.company  || '',
                  address1: data.defaultAddress.address1 || '',
                  address2: data.defaultAddress.address2 || '',
                  zip:      data.defaultAddress.zip      || '',
                  city:     data.defaultAddress.city     || '',
                  country:  data.defaultAddress.country  || '',
                };
                patch.defaultAddressId = data.defaultAddress.id || null;
              }
              if (data.addresses) patch.addresses = data.addresses;
              patchSession(patch);
              loadAddresses(session);
            } else {
              defaultBtn.disabled = false;
            }
          })
          .catch(function () { defaultBtn.disabled = false; });
        return;
      }

      // Deactivate (soft — Leitbild UC-11)
      if (deleteBtn) {
        var id = parseInt(deleteBtn.dataset.id, 10);
        if (!confirm(window.customStrings.addrDeactivate)) return;
        deleteBtn.disabled = true;
        fetch(API_BASE + '/api/b2b/addresses/' + id + '?email=' + encodeURIComponent(session.email) + '&customerId=' + session.customerId, {
          method: 'DELETE',
        })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            if (data.success) {
              if (data.addresses) patchSession({ addresses: data.addresses });
              loadAddresses(session);
            } else {
              deleteBtn.disabled = false;
              showAddressSectionMsg(data.error || window.customStrings.errorShort, 'error');
            }
          })
          .catch(function () { deleteBtn.disabled = false; });
        return;
      }

      // Reactivate (Leitbild UC-11)
      if (reactivateBtn) {
        var id = parseInt(reactivateBtn.dataset.id, 10);
        reactivateBtn.disabled = true;
        fetch(API_BASE + '/api/b2b/addresses/' + id + '/reactivate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: session.email, customerId: session.customerId }),
        })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            if (data.success) {
              if (data.addresses) patchSession({ addresses: data.addresses });
              loadAddresses(session);
            } else {
              reactivateBtn.disabled = false;
              showAddressSectionMsg(data.error || window.customStrings.errorShort, 'error');
            }
          })
          .catch(function () { reactivateBtn.disabled = false; });
        return;
      }

      // Leitbild UC-12: Open Änderungsantrag form for billing/main
      var changeRequestBtn = e.target.closest('.konto-addr-change-request');
      if (changeRequestBtn) {
        var id = changeRequestBtn.dataset.id;
        var form = elAddressesContent.querySelector('.konto-addr-change-form[data-id="' + id + '"]');
        if (form) { show(form); changeRequestBtn.style.display = 'none'; }
        return;
      }

      var changeCancelBtn = e.target.closest('.konto-addr-change-cancel');
      if (changeCancelBtn) {
        var id = changeCancelBtn.dataset.id;
        var form = elAddressesContent.querySelector('.konto-addr-change-form[data-id="' + id + '"]');
        var openBtn = elAddressesContent.querySelector('.konto-addr-change-request[data-id="' + id + '"]');
        if (form) { hide(form); form.reset(); }
        if (openBtn) openBtn.style.display = '';
        return;
      }
    });

    // ── Inline edit form submit ───────────────────────────────
    elAddressesContent.addEventListener('submit', function (e) {
      var form = e.target.closest('.konto-addr-edit-form');
      if (!form) return;
      e.preventDefault();

      var id        = parseInt(form.dataset.id, 10);
      var submitBtn = form.querySelector('button[type="submit"]');
      var msgEl     = form.querySelector('.konto-addr-edit-msg');
      submitBtn.disabled = true;
      submitBtn.textContent = window.customStrings.saving;

      var data = {};
      form.querySelectorAll('[name]').forEach(function (el) { data[el.name] = el.value.trim(); });

      fetch(API_BASE + '/api/b2b/addresses/' + id, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.assign({ email: session.email, customerId: session.customerId }, data)),
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          submitBtn.disabled = false;
          submitBtn.textContent = window.customStrings.acSave;
          if (res.ok && res.data.success) {
            renderAddresses(res.data.addresses || [], session);
            patchSession({ addresses: res.data.addresses || [] });
          } else {
            showAddressMsg(msgEl, res.data.error || window.customStrings.errorShort, 'error');
          }
        })
        .catch(function () {
          submitBtn.disabled = false;
          submitBtn.textContent = window.customStrings.acSave;
          showAddressMsg(msgEl, window.customStrings.netShort, 'error');
        });
    });

    // ── Änderungsantrag submit (UC-12) ────────────────────────
    elAddressesContent.addEventListener('submit', function (e) {
      var form = e.target.closest('.konto-addr-change-form');
      if (!form) return;
      e.preventDefault();

      var addressType = form.dataset.type || '';
      var submitBtn   = form.querySelector('button[type="submit"]');
      var msgEl       = form.querySelector('.konto-addr-change-msg');
      submitBtn.disabled = true;
      submitBtn.textContent = window.customStrings.crSubmitting;

      var changes = {};
      ['streetName', 'houseNumber', 'zip', 'city', 'country'].forEach(function (f) {
        var el = form.querySelector('[name="' + f + '"]');
        if (el && el.value.trim()) changes[f] = el.value.trim();
      });
      var commentEl = form.querySelector('[name="comment"]');
      var comment = commentEl ? commentEl.value.trim() : '';

      fetch(API_BASE + '/api/b2b/address-change-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: session.email, customerId: session.customerId, addressType: addressType, requestedChanges: changes, comment: comment }),
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          if (res.ok && res.data.success) {
            showAddressMsg(msgEl, res.data.message || window.customStrings.crSubmitted, 'success');
            submitBtn.style.display = 'none';
          } else {
            submitBtn.disabled = false;
            submitBtn.textContent = window.customStrings.crSubmit;
            showAddressMsg(msgEl, res.data.error || window.customStrings.crSubmitError, 'error');
          }
        })
        .catch(function () {
          submitBtn.disabled = false;
          submitBtn.textContent = window.customStrings.crSubmit;
          showAddressMsg(msgEl, window.customStrings.netShort, 'error');
        });
    });
  }

  function showAddressMsg(el, text, type) {
    if (!el) return;
    el.textContent = text;
    el.style.background = type === 'success' ? '#d1fae5' : '#fee2e2';
    el.style.color      = type === 'success' ? '#065f46' : '#991b1b';
    el.style.border     = '1px solid ' + (type === 'success' ? '#6ee7b7' : '#fca5a5');
    el.style.display    = 'block';
  }

  function showAddressSectionMsg(text, type) {
    showAddressMsg(document.getElementById('konto-addr-msg'), text, type);
  }

  // ── Logout ───────────────────────────────────────────────────
  function doLogout() {
    try { localStorage.removeItem(SESSION_KEY); } catch (e) {}
    if (window.B2BPriceCache) window.B2BPriceCache.clearAll();
    window.location.href = window.localizeUrl('/');
  }

  // ── Password Reset Form ──────────────────────────────────────
  function renderResetForm(token) {
    var page = document.querySelector('.konto-page');
    if (!page) return;

    page.innerHTML = ''
      + '<div style="max-width:420px;margin:4rem auto;padding:2rem">'
      + '<h1 class="konto-header__title" style="margin-bottom:1.5rem">' + window.customStrings.newPasswordTitle + '</h1>'
      + '<div id="reset-msg" style="display:none;padding:.8rem 1rem;border-radius:4px;margin-bottom:1rem;font-size:1.4rem"></div>'
      + '<form id="reset-pw-form" novalidate>'
      + '<div style="margin-bottom:1rem">'
      + '<input type="password" id="reset-pw1" placeholder="' + window.customStrings.newPasswordPh + '"'
      + ' style="width:100%;padding:.8rem 1rem;border:1px solid #ccc;border-radius:2px;font-size:1.4rem;box-sizing:border-box" required>'
      + '</div>'
      + '<div style="margin-bottom:1.5rem">'
      + '<input type="password" id="reset-pw2" placeholder="' + window.customStrings.passwordRepeatPh + '"'
      + ' style="width:100%;padding:.8rem 1rem;border:1px solid #ccc;border-radius:2px;font-size:1.4rem;box-sizing:border-box" required>'
      + '</div>'
      + '<button type="submit" id="reset-submit"'
      + ' style="width:100%;padding:1rem;background:#333;color:#fff;border:none;border-radius:2px;font-size:1.3rem;font-weight:700;letter-spacing:.06em;text-transform:uppercase;cursor:pointer">'
      + window.customStrings.savePassword
      + '</button>'
      + '</form>'
      + '</div>';

    var form      = document.getElementById('reset-pw-form');
    var msgEl     = document.getElementById('reset-msg');
    var submitBtn = document.getElementById('reset-submit');

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var pw1 = document.getElementById('reset-pw1').value;
      var pw2 = document.getElementById('reset-pw2').value;

      if (pw1.length < 12) { showResetMsg(window.customStrings.pwMin8, 'error'); return; }
      if (pw1 !== pw2)    { showResetMsg(window.customStrings.pwMismatch2, 'error'); return; }

      submitBtn.disabled = true;
      submitBtn.textContent = window.customStrings.saving;

      fetch(API_BASE + '/api/b2b/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: token, password: pw1, password2: pw2, locale: b2bLocale() }),
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          if (res.ok && res.data.success) {
            form.style.display = 'none';
            showResetMsg(window.customStrings.passwordSaved, 'success');
            if (res.data.session) { localStorage.setItem(SESSION_KEY, JSON.stringify(res.data.session)); }
            setTimeout(function () {
              window.location.href = res.data.session ? window.localizeUrl('/pages/konto') : window.localizeUrl('/') + '?openModal=login';
            }, 1500);
          } else {
            showResetMsg(res.data.error || window.customStrings.resetFailed, 'error');
            submitBtn.disabled = false;
            submitBtn.textContent = window.customStrings.savePassword;
          }
        })
        .catch(function () {
          showResetMsg(window.customStrings.net1, 'error');
          submitBtn.disabled = false;
          submitBtn.textContent = window.customStrings.savePassword;
        });
    });

    function showResetMsg(text, type) {
      msgEl.textContent = text;
      msgEl.style.background = type === 'success' ? '#d1fae5' : '#fee2e2';
      msgEl.style.color      = type === 'success' ? '#065f46' : '#991b1b';
      msgEl.style.border     = '1px solid ' + (type === 'success' ? '#6ee7b7' : '#fca5a5');
      msgEl.style.display    = 'block';
    }
  }

  // ── Badge helpers ────────────────────────────────────────────
  function paymentBadge(status) {
    var map = {
      paid:               ['konto-status--paid',    window.customStrings.stPaid],
      pending:            ['konto-status--pending',  window.customStrings.stPending],
      refunded:           ['konto-status--refund',   window.customStrings.stRefunded],
      partially_refunded: ['konto-status--refund',   window.customStrings.stPartialRefund],
      voided:             ['konto-status--refund',   window.customStrings.stCancelled],
      authorized:         ['konto-status--neutral',  window.customStrings.stAuthorized],
      partially_paid:     ['konto-status--pending',  window.customStrings.stPartialPaid],
    };
    var entry = map[status] || ['konto-status--neutral', status || '—'];
    return '<span class="konto-status ' + entry[0] + '">' + esc(entry[1]) + '</span>';
  }

  function fulfillBadge(status) {
    var map = {
      fulfilled:   ['konto-status--paid',    window.customStrings.shDelivered],
      unfulfilled: ['konto-status--pending',  window.customStrings.shOpen],
      partial:     ['konto-status--pending',  window.customStrings.shPartial],
      restocked:   ['konto-status--refund',   window.customStrings.shRestocked],
    };
    var entry = map[status] || ['konto-status--neutral', status || '—'];
    return '<span class="konto-status ' + entry[0] + '">' + esc(entry[1]) + '</span>';
  }

  // ── Utility ──────────────────────────────────────────────────
  function show(el) { if (el) el.style.display = ''; }
  function hide(el) { if (el) el.style.display = 'none'; }

  function esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
})();
